import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Alert,
  TextField,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { ADMIN_NEWSLETTER_SUBSCRIBERS_QUERY } from "../../graphql/newsletter";
import AdminLoading from "../Components/AdminLoading";

type Sub = {
  id: string;
  fullName: string;
  email: string;
  gender?: string | null;
  phone?: string | null;
  address?: string | null;
  createdAt: string;
};

const NewsletterUsersAdminPage: React.FC = () => {
  const { data, loading, error } = useQuery(ADMIN_NEWSLETTER_SUBSCRIBERS_QUERY);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");

  const subs: Sub[] = data?.newsletterSubscribers || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subs;
    return subs.filter((s) =>
      `${s.fullName} ${s.email} ${s.phone || ""}`.toLowerCase().includes(q)
    );
  }, [subs, search]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const busy = loading;

  return (
    <>
      <AdminLoading open={busy} />
      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          mb={2}
          spacing={2}
        >
          <Typography variant="h5">Newsletter Users</Typography>

          <TextField
            size="small"
            placeholder="Search name/email/phone..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ width: { xs: "100%", md: 340 } }}
          />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load newsletter users: {error.message}
          </Alert>
        )}

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found.
                    </TableCell>
                  </TableRow>
                )}

                {paged.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>{s.fullName}</TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell>{s.gender || "-"}</TableCell>
                    <TableCell>{s.phone || "-"}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      {s.address || "-"}
                    </TableCell>
                    <TableCell>
                      {new Date(s.createdAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Box>
    </>
  );
};

export default NewsletterUsersAdminPage;
